import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import allroomiePreview from '../landingpage/boston-landing-optimized.jpg';
import AuthPanel from './AuthPanel';
import { getLoginPageInitialMode } from '../../utils/authRoutePolicy';

const TestLoginPage: React.FC = () => {
  const location = useLocation();
  const [bgLoaded, setBgLoaded] = useState(false);
  const [panelLayout, setPanelLayout] = useState({ width: 974, height: 458, scale: 1 });
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const availableSpaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelScale = useCallback(() => {
    if (!isDesktop || !availableSpaceRef.current || !panelRef.current) return;

    const naturalWidth = panelRef.current.offsetWidth;
    const naturalHeight = panelRef.current.offsetHeight;
    const availableWidth = availableSpaceRef.current.clientWidth;
    const availableHeight = availableSpaceRef.current.clientHeight;
    if (!naturalWidth || !naturalHeight || !availableWidth || !availableHeight) return;

    // Fit horizontally only. Tall forms remain readable and scroll with the page.
    const nextScale = Math.min(1, availableWidth / naturalWidth);
    const roundedScale = Math.round(nextScale * 1000) / 1000;
    setPanelLayout(previous => {
      if (
        previous.width === naturalWidth &&
        previous.height === naturalHeight &&
        previous.scale === roundedScale
      ) {
        return previous;
      }
      return {
        width: naturalWidth,
        height: naturalHeight,
        scale: roundedScale,
      };
    });
  }, [isDesktop]);

  useEffect(() => {
    const img = new Image();
    img.src = allroomiePreview;
    img.onload = () => {
      setBgLoaded(true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setPanelLayout(previous => ({ ...previous, scale: 1 }));
      return;
    }

    updatePanelScale();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePanelScale);
      return () => window.removeEventListener('resize', updatePanelScale);
    }

    const resizeObserver = new ResizeObserver(updatePanelScale);
    if (availableSpaceRef.current) resizeObserver.observe(availableSpaceRef.current);
    if (panelRef.current) resizeObserver.observe(panelRef.current);
    return () => resizeObserver.disconnect();
  }, [isDesktop, updatePanelScale]);

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundImage: `linear-gradient(rgba(76, 64, 62, 0.30), rgba(76, 64, 62, 0.30)), url(${allroomiePreview})`,
          backgroundColor: 'lightgray',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          pointerEvents: 'none',
          opacity: bgLoaded ? 1 : 0,
        }}
      />

      <Box
        sx={{
          minHeight: { xs: 'auto', md: 'calc(100dvh - var(--appbar-height, 64px))' },
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          paddingX: { xs: 2, md: 3 },
          paddingTop: { xs: 2, md: 3 },
          paddingBottom: { xs: 4, md: 3 },
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        <Box
          ref={availableSpaceRef}
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: `${panelLayout.width * panelLayout.scale}px` },
              height: { xs: 'auto', md: `${panelLayout.height * panelLayout.scale}px` },
              flexShrink: 0,
            }}
          >
            <Box
              ref={panelRef}
              sx={{
                width: { xs: '100%', md: '974px' },
                transform: { xs: 'none', md: `scale(${panelLayout.scale})` },
                transformOrigin: 'top left',
              }}
            >
              <AuthPanel
                clientType="C"
                initialMode={getLoginPageInitialMode(location.search)}
                matchGoogleButtonToPrimaryOnMobile
                shellContainerSx={{ mx: { xs: 0, md: 0 } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TestLoginPage;
